package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.FunDecl
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * I4：screen 级 fun 调用 arity / 命名实参编译期校验。
 *
 * 扫描所有 [ExprAst.CallExpr]，当 callee 能静态解析为 screen 级 fun（[ScreenAst.funs]）时：
 * - E614：命名实参 name 不在该 fun 的形参列表中
 * - E615：必填形参（无默认值）未被位置实参 / 命名实参覆盖
 *
 * 只处理 callee 形态：
 * - [ExprAst.FunRefExpr]：parser 在 forward-reference 命中时升格产出
 * - [ExprAst.IdentRef]：偶尔出现，按名查找 screen 级 fun
 *
 * 局部 fun / lambda 变量 / 通过返回值调用等形态第一版不处理（callee 非静态可解析）。
 */
class CallArityChecker(private val diag: DiagnosticCollector) {

    fun check(screen: ScreenAst) {
        val funTable: Map<String, FunDecl> = screen.funs.associateBy { it.name }
        if (funTable.isEmpty()) return
        screen.funs.forEach { walkStmts(it.body, funTable) }
        // 元素树属性表达式同样可能包含 fun 调用
        walkElementProps(screen.root, funTable)
    }

    private fun walkElementProps(
        element: top.wcpe.allincore.dsl.compiler.ast.ElementAst,
        funTable: Map<String, FunDecl>,
    ) {
        element.props.forEach { walkExpr(it.value, funTable) }
        element.modifiers.forEach { modifier ->
            modifier.fields.forEach { walkExpr(it.value, funTable) }
        }
        // RFC-RECOMPOSITION：conditional 的 subject / branch.condition 表达式同样可能含 fun 调用
        element.conditionalContent?.let { content ->
            content.subject?.let { walkExpr(it, funTable) }
            for (branch in content.branches) {
                branch.condition?.let { walkExpr(it, funTable) }
            }
        }
        // $for：iterable 表达式同样可能含 fun 调用
        element.forContent?.let { walkExpr(it.iterable, funTable) }
        top.wcpe.allincore.dsl.compiler.ast.ElementWalk.directDescendants(element).forEach {
            walkElementProps(it, funTable)
        }
    }

    private fun walkStmts(stmts: List<StmtAst>, funTable: Map<String, FunDecl>) {
        stmts.forEach { walkStmt(it, funTable) }
    }

    private fun walkStmt(s: StmtAst, funTable: Map<String, FunDecl>) {
        when (s) {
            is StmtAst.AssignStmt -> walkExpr(s.rhs, funTable)
            is StmtAst.ExprStmt -> walkExpr(s.call, funTable)
            is StmtAst.VarDeclStmt -> walkExpr(s.initializer, funTable)
            is StmtAst.IfStmt -> {
                walkExpr(s.cond, funTable)
                walkStmts(s.thenBody, funTable)
                walkStmts(s.elseBody, funTable)
            }
            is StmtAst.WhileStmt -> {
                walkExpr(s.cond, funTable)
                walkStmts(s.body, funTable)
            }
            is StmtAst.ForInStmt -> {
                walkExpr(s.iterable, funTable)
                walkStmts(s.body, funTable)
            }
            is StmtAst.WhenStmt -> walkExpr(s.whenExpr, funTable)
            is StmtAst.ReturnStmt -> s.value?.let { walkExpr(it, funTable) }
            is StmtAst.TryStmt -> {
                walkStmts(s.tryBody, funTable)
                walkStmts(s.catchBody, funTable)
                walkStmts(s.finallyBody, funTable)
            }
            // batch 语句：递归遍历 body（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> walkStmts(s.body, funTable)
            is StmtAst.FunDeclStmt -> walkStmts(s.decl.body, funTable)
            is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> Unit
        }
    }

    private fun walkExpr(e: ExprAst, funTable: Map<String, FunDecl>) {
        when (e) {
            is ExprAst.CallExpr -> {
                checkCallAgainstScreenFun(e, funTable)
                walkExpr(e.callee, funTable)
                e.args.forEach { walkExpr(it, funTable) }
                e.namedArgs.forEach { walkExpr(it.value, funTable) }
            }
            is ExprAst.AwaitExpr -> {
                e.netRequestCall?.let { walkExpr(it, funTable) }
                e.suspendCall?.let { walkExpr(it, funTable) }
                e.tailExpr?.let { walkExpr(it, funTable) }
            }
            is ExprAst.LambdaExpr -> {
                e.defaults.forEach { it?.let { d -> walkExpr(d, funTable) } }
                walkStmts(e.body, funTable)
            }
            is ExprAst.BinaryOp -> { walkExpr(e.left, funTable); walkExpr(e.right, funTable) }
            is ExprAst.UnaryOp -> walkExpr(e.operand, funTable)
            is ExprAst.MemberAccess -> walkExpr(e.target, funTable)
            is ExprAst.SafeMemberAccess -> walkExpr(e.target, funTable)
            // M10 Stage 6：`target!!` 透传 target
            is ExprAst.NonNullAssert -> walkExpr(e.target, funTable)
            is ExprAst.MemberCall -> {
                walkExpr(e.target, funTable)
                e.args.forEach { walkExpr(it, funTable) }
                e.namedArgs.forEach { walkExpr(it.value, funTable) }
            }
            is ExprAst.SafeMemberCall -> {
                walkExpr(e.target, funTable)
                e.args.forEach { walkExpr(it, funTable) }
            }
            is ExprAst.Elvis -> { walkExpr(e.left, funTable); walkExpr(e.right, funTable) }
            is ExprAst.Ternary -> {
                walkExpr(e.cond, funTable)
                walkExpr(e.thenExpr, funTable)
                walkExpr(e.elseExpr, funTable)
            }
            is ExprAst.IfExpr -> {
                walkExpr(e.cond, funTable)
                walkStmts(e.thenBody, funTable)
                walkStmts(e.elseBody, funTable)
            }
            is ExprAst.Interp -> e.parts.forEach {
                if (it is ExprAst.Interp.InterpPart.ExprPart) walkExpr(it.expr, funTable)
            }
            is ExprAst.ListLiteralExpr -> e.elements.forEach { walkExpr(it, funTable) }
            is ExprAst.MapLiteralExpr -> e.entries.forEach { walkExpr(it.value, funTable) }
            is ExprAst.RangeExpr -> { walkExpr(e.start, funTable); walkExpr(e.endValue, funTable) }
            is ExprAst.IndexExpr -> { walkExpr(e.target, funTable); walkExpr(e.index, funTable) }
            is ExprAst.BuiltinCallExpr -> {
                e.args.forEach { walkExpr(it, funTable) }
                // M6-h Round 2：BuiltinCallExpr 引入命名实参后，遍历漏网会导致
                // `toast(message = ::g())` 之类形态绕过 arity 校验。这里统一补齐。
                e.namedArgs.forEach { walkExpr(it.value, funTable) }
            }
            is ExprAst.NetRequestCall -> {
                e.payload.forEach { walkExpr(it.value, funTable) }
                e.callback?.let { walkExpr(it, funTable) }
                e.timeoutMs?.let { walkExpr(it, funTable) }
            }
            is ExprAst.NetSubscribeCall -> walkExpr(e.handler, funTable)
            is ExprAst.WhenExpr -> {
                walkExpr(e.subject, funTable)
                e.branches.forEach { br ->
                    br.values.forEach { walkExpr(it, funTable) }
                    walkStmts(br.body, funTable)
                }
                e.elseBody?.let { walkStmts(it, funTable) }
            }
            is ExprAst.IsExpr -> walkExpr(e.target, funTable)
            is ExprAst.Literal, is ExprAst.NullLit, is ExprAst.IdentRef,
            is ExprAst.LocalRef, is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef, is ExprAst.PropsRef,
            is ExprAst.ThemeRef -> Unit
            is ExprAst.FunRefExpr -> {
                // M6-e：`::fun(args)` 也需校验绑定实参的 arity / 命名实参合法性
                if (e.boundArgs.isNotEmpty() || e.boundNamedArgs.isNotEmpty()) {
                    checkBoundFunRef(e, funTable)
                }
                e.boundArgs.forEach { walkExpr(it, funTable) }
                e.boundNamedArgs.forEach { walkExpr(it.value, funTable) }
            }
        }
    }

    /** M6-e：对 `::fun(args)` 形态做 arity / 命名实参校验。 */
    private fun checkBoundFunRef(ref: ExprAst.FunRefExpr, funTable: Map<String, FunDecl>) {
        val decl = funTable[ref.name]
        if (decl == null) {
            diag.error(DslError.E739_FUN_REF_BOUND_ARG_REF_NOT_FOUND, ref.position, ref.name)
            return
        }
        // E766：bound 位置实参数不能超过形参数（允许少于：剩余形参由调用时实参补齐）
        val paramCount = decl.params.size
        if (ref.boundArgs.size > paramCount) {
            diag.error(
                DslError.E766_FUN_REF_BOUND_ARG_COUNT_EXCEEDED,
                ref.position,
                ref.boundArgs.size, ref.name, paramCount,
            )
        }
        // E741：命名实参 name 必须在形参列表
        val paramNameSet = decl.params.toSet()
        for (na in ref.boundNamedArgs) {
            if (na.name !in paramNameSet) {
                diag.error(DslError.E767_FUN_REF_BOUND_NAMED_ARG_UNKNOWN, na.position, na.name, ref.name)
            }
        }
    }

    /** 静态解析 callee → screen 级 FunDecl，命中则做 arity 校验。 */
    private fun checkCallAgainstScreenFun(
        call: ExprAst.CallExpr,
        funTable: Map<String, FunDecl>,
    ) {
        val calleeName = when (val c = call.callee) {
            is ExprAst.FunRefExpr -> c.name
            is ExprAst.IdentRef -> c.name
            else -> null
        } ?: return
        val decl = funTable[calleeName] ?: return

        val params = decl.params
        val defaults = decl.defaults
        val paramNameSet = params.toSet()

        // E614：命名实参 name 不在形参列表
        for (na in call.namedArgs) {
            if (na.name !in paramNameSet) {
                diag.error(DslError.E614_UNKNOWN_NAMED_ARG, na.position, na.name)
            }
        }

        // E615：必填形参（无默认值）必须被位置实参或命名实参覆盖
        val providedNames = call.namedArgs.mapTo(mutableSetOf()) { it.name }
        val posCount = call.args.size
        for (i in params.indices) {
            val name = params[i]
            val hasDefault = i < defaults.size && defaults[i] != null
            if (hasDefault) continue
            val coveredByPos = i < posCount
            val coveredByNamed = name in providedNames
            if (!coveredByPos && !coveredByNamed) {
                diag.error(DslError.E615_MISSING_REQUIRED_ARG, call.position, name)
            }
        }
    }
}
